import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { connect, useDispatch, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router';
import { PARAMETERS } from '@root/tracking/constants';
import {
  Card,
  OnlyInDesktopTablet,
  OnlyInMobile,
  ExpandableDescription,
  VideoPlayer,
  AddToSavedItemsButton,
  Notification,
} from '@components/shared';
import { Row, Col } from '@components/antd';
import routes from '@root/routes';
import { StoreState } from '@root/types';
import {
  landlordPageLoad as landlordPageLoadAction,
  galleryNavigated,
  landlordThumbnailClicked,
  savedBuildingOwnerNotificationLinkClicked,
} from '@store/actions/landlordPage';
import savedActions from '@store/actions/saved';
import {
  getLandlord,
  hasCurrentUser as hasCurrentUserSelector,
  getBuildingOwnerSaveStatus,
} from '@store/selectors';
import useMarketo from '@root/shared/useMarketo';
import BasicInfo from './BasicInfo';
import Portfolio from './Portfolio';
import Layout from '../Layout';
import RawImages from '../Building/Images/RawImages';
import s from './Landlord.module.less';

const mapState = (state: StoreState, ownProps) => ({
  landlord: getLandlord(state, ownProps.match.params.slug),
  hasCurrentUser: hasCurrentUserSelector(state),
  isSaved: getBuildingOwnerSaveStatus(state, ownProps.match.params.slug),
});

const mapDispatch = {
  landlordPageLoad: (slug: string) => landlordPageLoadAction({ slug }),
  toggleBuildingOwnerSave: (slug: string, shouldSave: boolean) =>
    savedActions.toggleBuildingOwnerSave(slug, shouldSave, {}),
  savedBuilingOwnerLinkClicked: () => savedBuildingOwnerNotificationLinkClicked,
};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

export const RawLandlord = ({
  landlord,
  hasCurrentUser,
  landlordPageLoad,
  isSaved,
  toggleBuildingOwnerSave,
  savedBuilingOwnerLinkClicked,
}: ReduxProps) => {
  const { t } = useTranslation('landlord');
  const dispatch = useDispatch();
  const { slug } = useParams<{ slug: string }>();

  useMarketo();

  useEffect(() => {
    landlordPageLoad(slug);
  }, []);

  const onToggleSuccess = (newlySaved: boolean) => {
    if (newlySaved) {
      Notification.saveSuccess({
        title: t('landlord:saved'),
        /* eslint-disable react/jsx-no-literals */
        text: (
          <Trans i18nKey="savedSuccessNotification" ns="landlord">
            You’ll get notified if a listing is available from this building owner. View or edit it
            in your
            <a href={routes.savedBuildingOwners} onClick={savedBuilingOwnerLinkClicked}>
              saved building owners
            </a>
            section.
          </Trans>
        ),
        /* eslint-enable react/jsx-no-literals */
      });
    }
  };

  return landlord ? (
    <Layout>
      <div className={s.headerContainer}>
        <h1 className={s.name}>{landlord.name}</h1>
        <div className={s.spacer}></div>
        {/* eslint-disable no-nested-ternary */}
        {hasCurrentUser ? (
          <div className={s.logoAndSaved}>
            {landlord.logo ? (
              <>
                <img
                  className={s.logo}
                  src={landlord.logo.mediumPath}
                  alt={landlord.logo.description}
                />
                <span className={s.headerDivider} />
              </>
            ) : null}
            <div className={s.savedButtonContainer}>
              <AddToSavedItemsButton
                isSaved={isSaved}
                onClick={async (shouldBeSaved: boolean) => {
                  await toggleBuildingOwnerSave(landlord.slug, shouldBeSaved);
                  onToggleSuccess(shouldBeSaved);
                }}
              />
            </div>
          </div>
        ) : landlord.logo ? (
          <img className={s.logo} src={landlord.logo.mediumPath} alt={landlord.logo.description} />
        ) : null}
        {/* eslint-enable no-nested-ternary */}
      </div>
      <OnlyInDesktopTablet>
        <div className={s.line}></div>
        <BasicInfo
          inventory={landlord.inventory}
          premierRetailDestinations={landlord.premierRetailDestinations}
          space={landlord.space}
          underConstruction={landlord.underConstruction}
        />
      </OnlyInDesktopTablet>
      <div className={s.mediaAssets}>
        <RawImages
          photos={landlord.photos}
          video={landlord.video}
          lightboxPaginationClicked={() => dispatch(galleryNavigated)}
          lightboxThumbnailClicked={() => dispatch(landlordThumbnailClicked)}
          sourcePage={PARAMETERS.landlordPage}
          sourceContent={PARAMETERS.brandGallery}
          firstPhotoOrientation={landlord.firstPhotoOrientation}
          imagesZipUrl={routes.api.landlordImagesZipUrl(landlord.slug)}
        />
      </div>
      <OnlyInMobile>
        <BasicInfo
          inventory={landlord.inventory}
          premierRetailDestinations={landlord.premierRetailDestinations}
          space={landlord.space}
          underConstruction={landlord.underConstruction}
        />
        <div className={s.line}></div>
      </OnlyInMobile>
      <Row gutter={16}>
        <Col lg={16} md={16} sm={24}>
          {/* eslint-disable react/no-danger */}
          <ExpandableDescription>
            <div
              className={s.description}
              dangerouslySetInnerHTML={{ __html: landlord.description }}
            />
          </ExpandableDescription>
          {/* eslint-enable react/no-danger */}
          {landlord.video ? (
            <OnlyInMobile>
              <VideoPlayer
                video={landlord.video}
                className={s.mobileVideo}
                sourcePage={PARAMETERS.landlordPage}
                sourceContent={PARAMETERS.spotlightTour}
                testId="mobileVideo"
              />
            </OnlyInMobile>
          ) : null}
        </Col>
        {landlord.executives && landlord.executives.length > 0 ? (
          <Col className={s.cardColumn} lg={8} md={8} sm={24}>
            <Card title={t('keyExecutives')}>
              <ul className={s.keyExecutiveList}>
                {landlord.executives.map(executive => (
                  <li key={executive}>{executive}</li>
                ))}
              </ul>
            </Card>
          </Col>
        ) : null}
      </Row>
      <Portfolio landlordSlug={landlord.slug} marketSlugs={landlord.marketSlugs} />
    </Layout>
  ) : null;
};

export default connector(RawLandlord);
